import { Stack, createStyles, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { IconId } from '@tabler/icons-react';

import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Button } from '~/domains/design-system/Button';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import dt from '~/testing';

const useStyles = createStyles((theme) => ({
  iconCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: theme.colors.gray[1],
  },
}));

type ConfirmationDrawerProps = {
  onCancel?: () => void;
  onClose: () => void;
  onConfirm: () => void;
};

export default function ConfirmationDrawer({
  onCancel,
  onClose,
  onConfirm,
}: ConfirmationDrawerProps) {
  const { t } = useTranslation('auth');
  const isMobile = useIsMobile();
  const { classes, theme } = useStyles();

  return (
    <Stack
      spacing={theme.other.spacing._32}
      sx={{
        paddingLeft: theme.other.spacing._24,
        paddingRight: theme.other.spacing._24,
        paddingBottom: theme.other.spacing._32,
        paddingTop: isMobile ? 0 : theme.other.spacing._24,
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Stack spacing={theme.other.spacing._16} align="center" sx={{ width: '100%' }}>
        <div className={classes.iconCircle}>
          <IconId size={28} color={theme.colors.gray[7]} />
        </div>
        <Stack spacing={theme.other.spacing._8} sx={{ width: '100%' }}>
          <Title
            data-test-id={dt.components.shared.auth.signUpVersionTwo.confirmationModal.title}
            variant="subhead-x-large"
            weight={700}
            c="gray2.7"
          >
            {t('signUp.confirmation.title')}
          </Title>
          <Text
            data-test-id={dt.components.shared.auth.signUpVersionTwo.confirmationModal.description}
            variant="title-medium"
            c="gray2.5"
          >
            {t('signUp.confirmation.copy')}
          </Text>
        </Stack>
      </Stack>
      <Stack spacing={theme.other.spacing._16} sx={{ width: '100%' }}>
        <Button
          data-test-id={dt.components.shared.auth.signUpVersionTwo.confirmationModal.confirmButton}
          variant="primary-fill"
          size="large-icon"
          onClick={() => {
            onConfirm?.();
            onClose();
          }}
        >
          {t('signUp.confirmation.confirmButton')}
        </Button>
        <Button
          data-test-id={dt.components.shared.auth.signUpVersionTwo.confirmationModal.goBackButton}
          variant="tertiary-fill"
          onClick={() => {
            onCancel?.();
            onClose();
          }}
        >
          {t('signUp.confirmation.goBackButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
